import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { z } from 'zod';
import { ErroApi } from '../api/cliente';
import { auth } from '../api/endpoints';
import { Aviso } from '../components/Aviso';
import { Botao } from '../components/Botao';
import { Campo } from '../components/Campo';
import { Pagina } from '../components/Pagina';
import { useAtualizarSessao, useSessao } from '../hooks/useSessao';

const esquema = z
  .object({
    senhaAtual: z.string().min(1, 'Informe a senha atual.'),
    novaSenha: z.string().min(8, 'A senha precisa ter pelo menos 8 caracteres.').max(72, 'A senha pode ter no máximo 72 caracteres.'),
    confirmacao: z.string(),
  })
  .refine((d) => d.novaSenha === d.confirmacao, { path: ['confirmacao'], message: 'As senhas não conferem.' })
  .refine((d) => d.novaSenha !== d.senhaAtual, { path: ['novaSenha'], message: 'A nova senha precisa ser diferente da atual.' });
type Dados = z.infer<typeof esquema>;

export function TrocarSenha() {
  const { data: sessao } = useSessao();
  const atualizarSessao = useAtualizarSessao();
  const navegar = useNavigate();
  const [erro, setErro] = useState<string | null>(null);
  const [trocada, setTrocada] = useState(false);
  const { register, handleSubmit, formState, setError, reset } = useForm<Dados>({ resolver: zodResolver(esquema), mode: 'onBlur' });
  const obrigatoria = sessao?.usuario.deveTrocarSenha;

  async function salvar(d: Dados) {
    setErro(null);
    setTrocada(false);
    try {
      atualizarSessao(await auth.trocarSenha(d.senhaAtual, d.novaSenha));
      // Troca obrigatória: segue para o sistema. Troca voluntária: confirma aqui mesmo.
      if (obrigatoria) navegar('/', { replace: true });
      else {
        reset();
        setTrocada(true);
      }
    } catch (e) {
      if (e instanceof ErroApi && e.doCampo('senhaAtual')) setError('senhaAtual', { message: e.doCampo('senhaAtual') });
      else if (e instanceof ErroApi && e.doCampo('novaSenha')) setError('novaSenha', { message: e.doCampo('novaSenha') });
      else setErro(e instanceof ErroApi ? e.message : 'Não foi possível trocar a senha. Tente de novo.');
    }
  }

  return (
    <Pagina titulo="Trocar senha" contexto={obrigatoria ? undefined : 'Sua sessão continua aberta depois da troca.'}>
      <form onSubmit={handleSubmit(salvar)} noValidate className="flex max-w-sm flex-col gap-4">
        {obrigatoria && (
          <Aviso tipo="aviso" titulo="Crie uma senha própria para continuar">
            Você entrou com uma senha temporária. Ela deixa de valer assim que você criar a nova.
          </Aviso>
        )}
        {erro && <Aviso tipo="erro">{erro}</Aviso>}
        {trocada && <Aviso tipo="ok">Senha alterada. Use a nova senha no próximo acesso.</Aviso>}
        <Campo rotulo={obrigatoria ? 'Senha temporária' : 'Senha atual'} type="password" autoComplete="current-password" erro={formState.errors.senhaAtual?.message} {...register('senhaAtual')} />
        <Campo rotulo="Nova senha" type="password" autoComplete="new-password" ajuda="Pelo menos 8 caracteres." erro={formState.errors.novaSenha?.message} {...register('novaSenha')} />
        <Campo rotulo="Repita a nova senha" type="password" autoComplete="new-password" erro={formState.errors.confirmacao?.message} {...register('confirmacao')} />
        <div className="flex gap-2 pt-1">
          <Botao type="submit" variante="principal" carregando={formState.isSubmitting}>
            Salvar nova senha
          </Botao>
          {!obrigatoria && (
            <Botao variante="discreto" onClick={() => navegar(-1)}>
              Cancelar
            </Botao>
          )}
        </div>
      </form>
    </Pagina>
  );
}
